import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, Download, Loader2, AlertTriangle, RefreshCw, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
    VIGENCIA: 'Renovación',
    RENEWAL: 'Renovación',
    EXPIRY: 'Renovación',
    DECLARATION_OF_USE: 'Declaración de uso',
    CUSTOM: 'Personalizada',
};

function formatDate(val) {
    if (!val) return '—';
    try {
        return new Date(val).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return val; }
}

export function ClientAlerts({ clientId, trademarkIds = [], token, onCountReady }) {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [filter, setFilter] = useState('all'); // all | pending | overdue

    const fetchAlerts = useCallback(async () => {
        if (!clientId) { setLoading(false); return; }
        setLoading(true);
        try {
            const res = await axios.get(`${API}/alerts`, {
                params: { client_id: clientId, status: 'PENDING' },
                headers: { Authorization: `Bearer ${token}` },
            });
            const all = Array.isArray(res.data) ? res.data : (res.data?.items ?? []);
            all.sort((a, b) => {
                if (a.is_overdue && !b.is_overdue) return -1;
                if (!a.is_overdue && b.is_overdue) return 1;
                return (a.due_date || '') < (b.due_date || '') ? -1 : 1;
            });
            setAlerts(all);
            if (onCountReady) onCountReady(all.length);
        } catch (err) {
            console.error('Error fetching client alerts:', err);
            toast.error('Error al cargar alertas del cliente');
        } finally {
            setLoading(false);
        }
    }, [clientId, token, onCountReady]);

    useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

    // Marcar alerta como completada/descartada con actualización optimista
    const handleCompleteAlert = async (alertId, newStatus) => {
        // Quitar de la lista inmediatamente (optimistic)
        setAlerts(prev => {
            const updated = prev.filter(a => a.id !== alertId);
            if (onCountReady) onCountReady(updated.length);
            return updated;
        });
        try {
            await axios.put(`${API}/alerts/${alertId}`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(newStatus === 'COMPLETED' ? 'Alerta marcada como completada ✓' : 'Alerta descartada');
        } catch (err) {
            console.error('Error updating alert:', err);
            toast.error('Error al actualizar alerta');
            fetchAlerts(); // revertir si falla
        }
    };

    const handleExportExcel = async () => {
        if (alerts.length === 0) { toast.warning('No hay alertas para exportar'); return; }
        setExporting(true);
        try {
            // Build CSV in browser (no backend endpoint needed)
            const headers = ['Marca', 'Expediente', 'Tipo', 'Estado', 'Fecha Límite', 'Días Restantes', 'Descripción'];
            const rows = visibleAlerts.map(a => [
                a.trademark_denominacion || '',
                a.trademark_expediente || '',
                TYPE_LABELS[a.alert_type] || a.alert_type || '',
                a.is_overdue ? 'Vencida' : 'Pendiente',
                a.due_date ? a.due_date.slice(0, 10) : '',
                a.days_remaining !== undefined ? a.days_remaining : '',
                a.description || '',
            ]);
            const csvContent = [headers, ...rows]
                .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
                .join('\n');
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `alertas_cliente_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Exportación completada');
        } catch (err) {
            toast.error('Error al exportar');
        } finally {
            setExporting(false);
        }
    };

    const visibleAlerts = alerts.filter(a => {
        if (filter === 'overdue') return a.is_overdue;
        if (filter === 'pending') return !a.is_overdue;
        return true;
    });

    const overdueCount = alerts.filter(a => a.is_overdue).length;
    const pendingCount = alerts.filter(a => !a.is_overdue).length;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-gray-300 mr-3" />
                <span className="text-sm text-gray-400 font-medium">Cargando alertas…</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header toolbar */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-amber-500" />
                    <span className="text-sm font-black text-gray-900">Alertas del cliente</span>
                    <span className="text-[10px] font-black px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{alerts.length} total</span>
                    {overdueCount > 0 && (
                        <span className="text-[10px] font-black px-2 py-0.5 bg-red-100 text-red-700 rounded-full border border-red-200">
                            {overdueCount} vencidas
                        </span>
                    )}
                    {pendingCount > 0 && (
                        <span className="text-[10px] font-black px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full border border-amber-200">
                            {pendingCount} pendientes
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {/* Filter pills */}
                    {['all', 'overdue', 'pending'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`text-[10px] font-black px-2.5 py-1 rounded-full border transition-colors ${filter === f
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            {f === 'all' ? 'Todas' : f === 'overdue' ? 'Vencidas' : 'Pendientes'}
                        </button>
                    ))}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportExcel}
                        disabled={exporting || alerts.length === 0}
                        className="h-8 text-[10px] font-black border-gray-200 text-gray-700 hover:bg-gray-50 gap-1.5"
                    >
                        {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                        Exportar Excel
                    </Button>
                    <button onClick={fetchAlerts} className="p-1.5 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-colors">
                        <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>

            {/* Alerts list */}
            {visibleAlerts.length === 0 ? (
                <div className="bg-white rounded-xl border border-dashed border-gray-200 p-12 text-center">
                    <Bell className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm font-bold text-gray-400">Sin alertas activas</p>
                    <p className="text-[10px] text-gray-300 mt-1 uppercase tracking-wider font-black">
                        Las alertas aparecen 180 días antes o 120 días después del vencimiento
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-2">
                    {visibleAlerts.map(alert => {
                        const isOverdue = alert.is_overdue;
                        const typeLabel = TYPE_LABELS[alert.alert_type] || alert.alert_type;
                        const badgeColor = isOverdue
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : 'bg-amber-100 text-amber-700 border-amber-200';
                        const cardBorder = isOverdue
                            ? 'border-red-100 hover:border-red-200'
                            : 'border-amber-100 hover:border-amber-200';

                        const daysVal = alert.days_remaining ?? alert.days_until_due;
                        const daysLabel = isOverdue
                            ? `Vencida hace ${Math.abs(daysVal ?? 0)} días`
                            : daysVal !== undefined ? `${daysVal} días restantes` : '';

                        return (
                            <div key={alert.id} className={`bg-white rounded-xl border ${cardBorder} p-4 shadow-sm transition-colors`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${isOverdue ? 'bg-red-50 text-red-500' : 'bg-amber-50 text-amber-500'}`}>
                                            <Bell className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${badgeColor}`}>
                                                    {typeLabel}
                                                </span>
                                            </div>
                                            {/* Marca */}
                                            {alert.trademark_denominacion && (
                                                <p className="text-sm font-black text-gray-900 truncate">{alert.trademark_denominacion}</p>
                                            )}
                                            {alert.trademark_expediente && (
                                                <p className="text-[10px] text-gray-400 font-mono font-bold">Exp. {alert.trademark_expediente}</p>
                                            )}
                                            {/* Fechas */}
                                            <div className="flex flex-wrap items-center gap-3 mt-1.5">
                                                {alert.due_date && (
                                                    <div className="flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">
                                                        <span className="text-[9px] text-gray-400 uppercase font-black">Límite</span>
                                                        <span className="text-[10px] text-gray-700 font-black">{formatDate(alert.due_date)}</span>
                                                    </div>
                                                )}
                                                {daysLabel && (
                                                    <span className={`text-[10px] font-bold ${isOverdue ? 'text-red-600' : 'text-amber-600'}`}>
                                                        {daysLabel}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Estado */}
                                    <span className={`shrink-0 text-[9px] font-black px-2 py-1 rounded-full border uppercase ${isOverdue ? 'bg-red-50 text-red-600 border-red-100' : 'bg-amber-50 text-amber-600 border-amber-100'
                                        }`}>
                                        {isOverdue ? 'Vencida' : 'Pendiente'}
                                    </span>
                                </div>
                                {/* Action buttons */}
                                <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100 mt-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-[10px] font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2"
                                        onClick={(e) => { e.stopPropagation(); window.open(`/trademarks/${alert.trademark_id}`, '_blank'); }}
                                    >
                                        <Eye className="h-3 w-3 mr-1" />
                                        Ver Marca
                                    </Button>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-[10px] font-bold bg-green-50 text-green-700 border-green-200 hover:bg-green-100 px-3"
                                            onClick={() => handleCompleteAlert(alert.id, 'COMPLETED')}
                                        >
                                            Atender
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-[10px] font-bold bg-white text-gray-500 border-gray-200 hover:bg-gray-50 px-3"
                                            onClick={() => handleCompleteAlert(alert.id, 'DISMISSED')}
                                        >
                                            Omitir
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
